import { useCallback, useState, useEffect } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { useColorMode, useSetColorMode } from '@atlaskit/app-provider';
import DashboardIcon from '@atlaskit/icon/core/dashboard';
import { ButtonItem, Section } from '@atlaskit/menu';
import { ThemeColorModes } from '@atlaskit/tokens';
import AccountMenu from '@atlassian/account-menu/nav4';

import { AccountMenuWorkspaces } from 'src/components/navigation/src/components/account-menu-workspaces';
import { updateIframeViewTheme } from 'src/components/navigation/src/components/theme-selector/utils';
import { usePref } from 'src/components/navigation/src/utils/use-pref';
import { WorkspaceLink } from 'src/components/workspace-link';
import { useStatsigGate } from 'src/contexts/feature-provider';
import { StatsigFeatureKeys } from 'src/features';
import { getUserGlobalTheme } from 'src/selectors/global-selectors';
import { settings } from 'src/settings';
import { publishUiEvent } from 'src/utils/analytics/publish';
import { atlassianStore } from 'src/utils/store';

import { useTopNavData } from './hooks';

const messages = defineMessages({
  tryNewDashboard: {
    id: 'frontbucket.topNav.profile.tryNewDashboard',
    description: 'Label for the button to opt in to the new dashboard',
    defaultMessage: 'Try the new dashboard',
  },
  switchToClassicDashboard: {
    id: 'frontbucket.topNav.profile.switchToClassicDashboard',
    description: 'Label for the button to switch back to the classic dashboard',
    defaultMessage: 'Switch to classic dashboard',
  },
});

export const TopNavProfile = (): JSX.Element => {
  const {
    userDisplayName,
    userAvatarUrl,
    userEmail,
    loginUrl,
    logoutUrl,
    manageAccountUrl,
    switchAccountUrl,
    isCurrentUserAnonymous,
    userWorkspaceId,
    recentlyViewedWorkspacesKeys,
  } = useTopNavData();
  const intl = useIntl();
  const colorMode = useColorMode();
  const setColorMode = useSetColorMode();
  const [, updateUserGlobalTheme] = usePref('global-theme', true);
  const prefValue = useSelector(getUserGlobalTheme);
  const [localTheme, setLocalTheme] = useState<ThemeColorModes>(
    (prefValue ?? colorMode) as ThemeColorModes
  );

  const isNewDashboardGateEnabled = useStatsigGate(
    StatsigFeatureKeys.bbcNewDashboard
  );

  const isNewDashboardDefaultOptInEnabled = useStatsigGate(
    StatsigFeatureKeys.bbcNewDashboardDefaultOptIn
  );

  useEffect(() => {
    if (
      isNewDashboardGateEnabled &&
      isNewDashboardDefaultOptInEnabled &&
      atlassianStore.get('new-dashboard-opt-in') === undefined
    ) {
      atlassianStore.setStrictlyNecessaryItem('new-dashboard-opt-in', true);
    }
  }, [isNewDashboardGateEnabled, isNewDashboardDefaultOptInEnabled]);

  const isNewDashboardEnabled =
    atlassianStore.get('new-dashboard-opt-in') === true;

  const handleToggleNewDashboard = useCallback(() => {
    const next = !isNewDashboardEnabled;
    publishUiEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: next ? 'newDashboardOptIn' : 'newDashboardOptOut',
      source: 'navigation',
    });
    atlassianStore.setStrictlyNecessaryItem('new-dashboard-opt-in', next);
    window.location.reload();
  }, [isNewDashboardEnabled]);

  const handleThemeChange = useCallback(
    (value: ThemeColorModes) => {
      // set local state for accurate theme tracking in case "auto" is selected
      setLocalTheme(value);
      // sets correct color for AppProvider (which handles updating tokens)
      setColorMode(value);
      // save to DB (user prefs)
      updateUserGlobalTheme(value);
      // also ensure iframes are updated if need
      updateIframeViewTheme(value);
    },
    [setColorMode, updateUserGlobalTheme]
  );

  return (
    <AccountMenu
      name={userDisplayName}
      picture={userAvatarUrl}
      email={userEmail}
      logOutLink={logoutUrl || ''}
      logInLink={
        isCurrentUserAnonymous && loginUrl
          ? // we need to use an absolute URL to force a non-SPA navigation
            // when pages are not yet supported in Frontbucket
            new URL(loginUrl, settings().CANON_URL).toString()
          : undefined
      }
      manageProfileLink={manageAccountUrl}
      switchAccountLink={switchAccountUrl}
      currentTheme={localTheme}
      onThemeChange={handleThemeChange}
      extraMenuItems={
        <Section>
          {isNewDashboardGateEnabled && (
            <ButtonItem
              onClick={handleToggleNewDashboard}
              iconBefore={<DashboardIcon label="" />}
            >
              {isNewDashboardEnabled
                ? intl.formatMessage(messages.switchToClassicDashboard)
                : intl.formatMessage(messages.tryNewDashboard)}
            </ButtonItem>
          )}
          {recentlyViewedWorkspacesKeys ? (
            <AccountMenuWorkspaces
              key="workspaces"
              userWorkspaceSlug={userWorkspaceId}
              recentlyViewedWorkspaces={recentlyViewedWorkspacesKeys}
              workspaceLinkComponent={WorkspaceLink}
              hasSeparator
            />
          ) : undefined}
        </Section>
      }
    />
  );
};
